<template>
  <div class="global-player" :class="{ 'is-active': currentTrack }">
    <div v-if="currentTrack" class="player-content">
      <!-- Track Info -->
      <div class="track-info">
        <div class="track-thumb">
          <img v-if="currentTrack.imageUrl" :src="currentTrack.type === 'suno' ? currentTrack.imageUrl : getProxiedUrl(currentTrack.imageUrl)" alt="Cover" />
          <div v-else class="thumb-placeholder">
            <svg viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55C7.79 13 6 14.79 6 17s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>
          </div>
        </div>
        <div class="track-details">
          <div class="track-title">{{ currentTrack.title }}</div>
          <div class="track-artist">{{ hasPreviewBounds ? $t('player.preview') : (currentTrack?.artist || '') }}</div>
        </div>
      </div>

      <!-- Controls -->
      <div class="controls">
        <button @click="player.toggle(currentTrack)" class="ctrl-btn play-btn" :title="isPlaying ? 'Pause' : 'Play'">
          <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 ml-0.5"><path d="M8 5v14l11-7z"/></svg>
          <svg v-else viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
        </button>
        <button @click="player.stop" class="ctrl-btn secondary-btn" title="Stop">
          <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M6 6h12v12H6z"/></svg>
        </button>
        <button @click="looping = !looping" class="ctrl-btn secondary-btn" :class="{ active: looping }" title="Loop">
          <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/></svg>
        </button>
      </div>

      <!-- Waveform + Progress -->
      <div class="progress-area">
        <span class="time-label">{{ formatTime(hasPreviewBounds ? Math.max(0, currentTime - previewStart) : currentTime) }}</span>
        <div class="waveform-area">
          <ClientOnly>
            <SharedWaveformCanvas
              v-if="!isLoadingPeaks"
              :peaks="peaksData"
              :progress="waveProgress"
              :region-start="previewRegionStart"
              :region-end="previewRegionEnd"
              :height="32"
              :bar-width="2"
              :gap="1"
              color="rgba(255,255,255,0.15)"
              active-color="#00f2ff"
              @seek="handleSeek"
            />
            <div v-else class="waveform-loading"></div>
          </ClientOnly>
        </div>
        <span class="time-label">{{ formatTime(hasPreviewBounds ? previewLength : duration) }}</span>
      </div>

      <!-- Volume -->
      <div class="volume-control">
        <button class="ctrl-btn secondary-btn" @click="toggleMute" :title="volume === 0 ? 'Unmute' : 'Mute'">
          <svg v-if="volume === 0" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
          <svg v-else-if="volume < 0.5" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"/></svg>
          <svg v-else viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
        </button>
        <div class="vol-popup">
          <input
            type="range"
            min="0" max="1" step="0.01"
            v-model.number="volume"
            class="vol-slider"
            :style="{ '--vol-percent': (volume * 100) + '%' }"
          />
        </div>
      </div>

      <!-- Close -->
      <button class="ctrl-btn close-btn" @click="closePlayer" title="Close">
        <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'

const player = useAudioPlayer()
const { currentTrack, isPlaying, currentTime, duration, volume } = player

const audioElement = ref(null)
const peaksData = ref([])
const isLoadingPeaks = ref(false)
const looping = ref(false)
const previousVolume = ref(1)

// Initialize Audio Element
if (process.client) {
    audioElement.value = new Audio()
    audioElement.value.preload = 'auto'
    audioElement.value.volume = volume.value
}

const closePlayer = () => {
  player.stop()
  currentTrack.value = null
}

const toggleMute = () => {
  if (volume.value > 0) {
    previousVolume.value = volume.value
    volume.value = 0
  } else {
    volume.value = previousVolume.value || 1
  }
}

const formatTime = (seconds) => {
  if (!seconds) return '0:00'
  const min = Math.floor(seconds / 60)
  const sec = Math.floor(seconds % 60)
  return `${min}:${sec.toString().padStart(2, '0')}`
}

// --- Preview Bounds ---
const hasPreviewBounds = computed(() => !!currentTrack.value?.previewLength)
const previewStart = computed(() => currentTrack.value?.previewStart || 0)
const previewLength = computed(() => currentTrack.value?.previewLength || 0)
const previewEnd = computed(() => previewStart.value + previewLength.value)
// Where the preview slice sits inside the FULL track. The audio element is
// fed only the window (see /api/shop/preview/:id), so its clock starts at 0
// and previewStart/previewEnd address the slice — but the waveform still
// draws the whole song, so anything measured against it has to add the
// offset back. Zero for anything that plays a complete file.
const previewOffset = computed(() => currentTrack.value?.previewOffset || 0)

// ── Preview window on the waveform ────────────────────────────────
// The waveform shows the WHOLE track on purpose — a buyer should see what
// they are buying — but only the preview window is playable. So progress and
// seeking are measured against the full duration, and the canvas marks the
// window rather than pretending the track is only that long.
//
// `duration` is not usable for this: it is deliberately overwritten with
// previewLength so the time labels count the preview, not the file.
const audioFullDuration = ref(0)
const fullDuration = computed(() => previewOffset.value > 0
    ? (currentTrack.value?.duration || audioFullDuration.value || 0)
    : (audioFullDuration.value || currentTrack.value?.duration || 0))

const waveProgress = computed(() => {
    if (!hasPreviewBounds.value) return duration.value ? currentTime.value / duration.value : 0
    return fullDuration.value ? (previewOffset.value + currentTime.value) / fullDuration.value : 0
})
const previewRegionStart = computed(() =>
    hasPreviewBounds.value && fullDuration.value ? (previewOffset.value + previewStart.value) / fullDuration.value : 0)
const previewRegionEnd = computed(() =>
    hasPreviewBounds.value && fullDuration.value ? (previewOffset.value + previewEnd.value) / fullDuration.value : 1)


// --- Audio Logic ---

const loadAndPlay = async () => {
    if (!audioElement.value || !currentTrack.value) return

    audioElement.value.pause()
    audioElement.value.currentTime = 0

    const url = currentTrack.value.type === 'suno'
        ? currentTrack.value.url
        : getProxiedUrl(currentTrack.value.url)
    audioElement.value.src = url
    audioElement.value.load()

    loadPeaks()

    if (hasPreviewBounds.value) {
        audioElement.value.currentTime = previewStart.value
        currentTime.value = previewStart.value
    }

    if (isPlaying.value) {
        try {
            await audioElement.value.play()
        } catch(e) {
            console.error("Playback failed", e)
            isPlaying.value = false
        }
    }
}

// Monotonic counter so an in-flight peaks fetch from a previous track
// can't overwrite the current track's waveform when the user clicks
// "Play" on a second track before the first one's fetch resolves.
let peaksRequestSeq = 0

const loadPeaks = async () => {
    isLoadingPeaks.value = true
    peaksData.value = []
    const mySeq = ++peaksRequestSeq

    const track = currentTrack.value
    if (!track) {
        // Early return MUST reset the loading flag — the template's
        // `v-if="!isLoadingPeaks"` would otherwise leave the waveform
        // hidden forever once a track is closed mid-load.
        isLoadingPeaks.value = false
        return
    }

    let rawPeaks = []

    // 1) Inline waveform (preferred — already on the track payload).
    if (track.audio && Array.isArray(track.audio.waveform) && track.audio.waveform.length > 0) {
        rawPeaks = track.audio.waveform
    } else {
        // 2) Explicit peaksUrl shape (legacy / non-shop tracks like Suno).
        let peaksUrl = track.peaksUrl
        if (!peaksUrl && track.audio?.peaks?.url) peaksUrl = track.audio.peaks.url

        if (peaksUrl) {
            const url = getProxiedUrl(peaksUrl)
            try {
                const res = await fetch(url)
                if (res.ok) {
                    const json = await res.json()
                    rawPeaks = json.data || []
                }
            } catch (e) { console.error("Peaks load error", e) }
        } else if (track.id && !String(track.id).startsWith('suno_') && track.type !== 'suno') {
            // 3) Lazy-fetch from the shop endpoint when we have a track id
            //    but no inline waveform — the case for hero/release/listing
            //    tracks whose populate intentionally drops the 4000-float
            //    array to keep the listing payload light. Skip for Suno
            //    (synthetic ids that don't exist in the Track collection).
            try {
                const res = await $fetch(`/api/shop/tracks/${encodeURIComponent(track.id)}/waveform`)
                if (Array.isArray(res?.waveform)) rawPeaks = res.waveform
            } catch (e) {
                // 404/non-shop track or network blip — fall through to
                // the fake waveform so the player still renders something.
            }
        }
    }

    // Bail out if a newer load started while we were awaiting — that
    // request will set peaksData and the loading flag itself, and we
    // don't want to overwrite its result with our stale data.
    if (mySeq !== peaksRequestSeq) return

    if (rawPeaks.length > 0) {
        peaksData.value = normalizeWaveform(rawPeaks)
    }
    if (peaksData.value.length === 0) {
        peaksData.value = generateFakeWaveform(100)
    }
    isLoadingPeaks.value = false
}

// Events
onMounted(() => {
    if (!audioElement.value) return

    const audio = audioElement.value

    audio.ontimeupdate = () => {
        currentTime.value = audio.currentTime
        if (hasPreviewBounds.value && audio.currentTime >= previewEnd.value) {
            if (looping.value) {
                audio.currentTime = previewStart.value
                currentTime.value = previewStart.value
            } else {
                audio.pause()
                isPlaying.value = false
                audio.currentTime = previewStart.value
                currentTime.value = previewStart.value
            }
        }
        // Fire-and-forget heartbeat — internal threshold check makes
        // this cheap (returns early until ≥30s of wall-clock elapsed).
        maybeSendHeartbeat()
    }

    audio.onloadedmetadata = () => {
        audioFullDuration.value = audio.duration || 0
        if (hasPreviewBounds.value) {
            duration.value = previewLength.value
        } else {
            duration.value = audio.duration
        }
    }

    audio.onended = () => {
        if (looping.value) {
            audio.currentTime = 0
            currentTime.value = 0
            audio.play().catch(() => {})
        } else {
            isPlaying.value = false
        }
    }

    audio.onerror = (e) => {
        console.error("Audio Error", e)
        isPlaying.value = false
    }
})

// Interaction
const handleSeek = (percent) => {
    if (!audioElement.value) return
    if (hasPreviewBounds.value) {
        // The click lands on a FULL-track waveform, so it maps to the full
        // duration and is then clamped into the preview. Mapping it onto the
        // preview length instead (as this did) meant a click at 90% of a
        // visibly full waveform jumped to 90% of the preview — the playhead
        // never went where the pointer was.
        const total = fullDuration.value || audioElement.value.duration || 0
        const target = percent * total - previewOffset.value
        const clamped = Math.min(Math.max(target, previewStart.value), previewEnd.value)
        audioElement.value.currentTime = clamped
        currentTime.value = clamped
    } else {
        const newTime = percent * (audioElement.value.duration || 0)
        audioElement.value.currentTime = newTime
        currentTime.value = newTime
    }
}

// ── Play telemetry ────────────────────────────────────────────────
// Per-track session state. `sessionKey` is the HMAC-signed token
// returned by /api/feed/tracks/:id/play/start; the heartbeat endpoint
// re-verifies it before bumping the play counter. `startedAtMs` is
// the wall-clock origin used to gate "≥30s played" client-side so we
// don't blast the heartbeat endpoint before it could possibly count.
// The server STILL re-measures from its own row's startedAt — the
// client clock is advisory.
const playSession = ref({
  trackId: null,
  sessionKey: null,
  startedAtMs: 0,
  // Wall-clock ms the audio element actually played FROM. Updated on
  // play / pause / seek to keep us honest about "30 seconds of real
  // playback" vs "30 seconds of wall-clock with the player paused".
  playedMs: 0,
  lastTickAtMs: 0,
  // 'idle' → not yet sent; 'sending' → request in flight; 'sent' →
  // counted; 'failed' → transient failure, backed off until
  // backoffUntilMs.
  heartbeatState: 'idle',
  backoffUntilMs: 0
})
const PLAY_HEARTBEAT_THRESHOLD_MS = 30 * 1000
const PLAY_HEARTBEAT_BACKOFF_MS = 30 * 1000   // pause retries 30s after a failure

function resetPlaySession() {
  playSession.value = {
    trackId: null,
    sessionKey: null,
    startedAtMs: 0,
    playedMs: 0,
    lastTickAtMs: 0,
    heartbeatState: 'idle',
    backoffUntilMs: 0
  }
}

async function startPlaySession(trackId) {
  // Suno-generated tracks are not real Track rows on the feed — they
  // have synthetic ids like `suno_xyz`. Telemetry would 404. Skip.
  if (!trackId || String(trackId).startsWith('suno_')) {
    resetPlaySession()
    return
  }
  // Suno tracks set type === 'suno' on the input — also skip.
  if (currentTrack.value?.type === 'suno') {
    resetPlaySession()
    return
  }
  try {
    const res = await $fetch(`/api/feed/tracks/${trackId}/play/start`, {
      method: 'POST',
      body: { source: 'feed' }
    })
    playSession.value = {
      trackId: String(trackId),
      sessionKey: res?.sessionKey || null,
      startedAtMs: Date.now(),
      playedMs: 0,
      lastTickAtMs: Date.now(),
      heartbeatState: 'idle',
      backoffUntilMs: 0
    }
  } catch (err) {
    // Telemetry must never block playback — silent failure.
    resetPlaySession()
  }
}

async function maybeSendHeartbeat() {
  const s = playSession.value
  if (!s.sessionKey) return
  // Already counted — or in flight — don't loop.
  if (s.heartbeatState === 'sent' || s.heartbeatState === 'sending') return
  // After a transient failure, wait out the backoff window before retrying.
  if (s.heartbeatState === 'failed' && Date.now() < s.backoffUntilMs) return

  // Accumulate played-time delta. ontimeupdate ticks roughly every
  // 250ms while audio is actually progressing. We update playedMs
  // here so the threshold reflects actual listened time, not just
  // wall-clock since /play/start.
  const now = Date.now()
  if (isPlaying.value && s.lastTickAtMs > 0) {
    const delta = now - s.lastTickAtMs
    // Clamp to avoid a paused/idle gap (page in background, tab
    // throttled) being counted as listening. 1000ms is generous —
    // ontimeupdate fires at ~4-15Hz when playing.
    if (delta > 0 && delta < 1500) {
      s.playedMs += delta
    }
  }
  s.lastTickAtMs = now

  if (s.playedMs < PLAY_HEARTBEAT_THRESHOLD_MS) return

  s.heartbeatState = 'sending'
  try {
    await $fetch(`/api/feed/tracks/${s.trackId}/play/heartbeat`, {
      method: 'POST',
      body: {
        sessionKey: s.sessionKey,
        secondsPlayed: Math.floor(s.playedMs / 1000)
      }
    })
    s.heartbeatState = 'sent'
  } catch {
    // Transient failure — back off so we don't flood the endpoint
    // from every subsequent ontimeupdate tick.
    s.heartbeatState = 'failed'
    s.backoffUntilMs = Date.now() + PLAY_HEARTBEAT_BACKOFF_MS
  }
}

// Watchers
watch(currentTrack, (newTrack, oldTrack) => {
    if (newTrack) {
        if (!oldTrack || newTrack.id !== oldTrack.id) {
            loadAndPlay()
            startPlaySession(newTrack.id)
        }
    } else {
        if (audioElement.value) {
            audioElement.value.pause()
            audioElement.value.src = ''
        }
        peaksData.value = []
        resetPlaySession()
    }
})

watch(isPlaying, (playing) => {
    if (!audioElement.value || !audioElement.value.src) return
    if (playing) {
        audioElement.value.play().catch(e => console.error("Play error", e))
    } else {
        audioElement.value.pause()
    }
})

watch(volume, (vol) => {
    if (audioElement.value) audioElement.value.volume = vol
})

watch(currentTime, (newTime) => {
    if (!audioElement.value) return
    if (Math.abs(audioElement.value.currentTime - newTime) > 0.5) {
        audioElement.value.currentTime = newTime
    }
})

onBeforeUnmount(() => {
    if (audioElement.value) {
        audioElement.value.pause()
        audioElement.value.src = ''
        audioElement.value = null
    }
})
</script>

<style scoped>
.global-player {
  position: fixed;
  bottom: 0;
  left: 260px;
  /* Hug the chat panel on the right — it exposes its current width
     (56px collapsed / 360px open / 0px on routes without chat) via
     the --chat-panel-width CSS var set in useChatUI.ts. Without this
     the player overlaps the chat rail. Transition matches the chat's
     own open/close so the right edge slides smoothly. */
  right: var(--chat-panel-width, 0px);
  height: 72px;
  background: rgba(10, 10, 15, 0.92);
  backdrop-filter: blur(24px) saturate(1.2);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  z-index: 9999;
  transform: translateY(100%);
  /* `right 0.2s ease-out` matches the ChatPanel's own width transition
     (transition-[width] duration-200 ease-out) so the two slide in
     lockstep when the chat is toggled. The longer `transform` stays
     as-is for the initial slide-in-from-bottom. */
  transition:
    transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
    right 0.2s ease-out;
  display: flex;
  align-items: center;
}

.global-player.is-active {
  transform: translateY(0);
}

.player-content {
  width: 100%;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 16px;
}

/* --- Track Info --- */
.track-info {
  width: 200px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.track-thumb {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.track-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.2);
}

.track-details {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.track-title {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.8rem;
  letter-spacing: 0.01em;
}

.track-artist {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.35);
}

/* --- Controls --- */
.controls {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.ctrl-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.15s ease;
}

.play-btn {
  width: 40px;
  height: 40px;
  background: rgba(0, 242, 255, 0.12);
  border: 1px solid rgba(0, 242, 255, 0.25);
  color: #00f2ff;
}

.play-btn:hover {
  background: rgba(0, 242, 255, 0.2);
  border-color: rgba(0, 242, 255, 0.4);
  box-shadow: 0 0 16px rgba(0, 242, 255, 0.15);
}

.secondary-btn {
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.secondary-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.8);
}

.secondary-btn.active {
  background: rgba(0, 242, 255, 0.1);
  border-color: rgba(0, 242, 255, 0.3);
  color: #00f2ff;
}

/* --- Progress Area --- */
.progress-area {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.time-label {
  font-family: 'JetBrains Mono', 'SF Mono', 'Fira Code', monospace;
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.25);
  width: 42px;
  text-align: center;
  flex-shrink: 0;
  letter-spacing: 0.02em;
}

.waveform-area {
  flex: 1;
  height: 32px;
  display: flex;
  align-items: center;
}

.waveform-loading {
  width: 100%;
  height: 100%;
  opacity: 0.15;
  background: repeating-linear-gradient(90deg, rgba(255,255,255,0.15) 0, rgba(255,255,255,0.15) 2px, transparent 2px, transparent 4px);
  border-radius: 4px;
}

/* --- Volume --- */
.volume-control {
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  z-index: 20;
}

.vol-popup {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 48px;
  height: 130px;
  background: rgba(10, 10, 15, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 10px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  pointer-events: none;
  margin-bottom: 8px;
  backdrop-filter: blur(16px);
}

.vol-popup::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  height: 16px;
  background: transparent;
}

.volume-control:hover .vol-popup {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translateX(-50%) translateY(-4px);
}

.vol-slider {
  appearance: none;
  -webkit-appearance: none;
  width: 90px;
  height: 4px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  outline: none;
  background-image: linear-gradient(#00f2ff, #00f2ff);
  background-size: var(--vol-percent) 100%;
  background-repeat: no-repeat;
  cursor: pointer;
  transform: rotate(-90deg);
}

.vol-slider::-webkit-slider-thumb {
  appearance: none;
  -webkit-appearance: none;
  height: 12px;
  width: 12px;
  border-radius: 50%;
  background: #00f2ff;
  cursor: pointer;
  box-shadow: 0 0 8px rgba(0, 242, 255, 0.4);
  transition: transform 0.1s;
}

.vol-slider::-webkit-slider-thumb:hover {
  transform: scale(1.3);
}

/* --- Close --- */
.close-btn {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.25);
}

.close-btn:hover {
  color: rgba(255, 255, 255, 0.6);
}
</style>
